/**
 * @file SortOptionsMenu.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-23
 * @modified 2026-09-23
 *
 * A small popover for a header whose sort is really two questions.
 *
 * Most columns sort one way and a click is enough. A Customer column is not
 * one of those: it shows an account number and a name, and "sort by customer"
 * means one or the other. A plain click had to pick, and it picked the name —
 * so the people who think in account numbers, which is most of the office, had
 * no way to get the order they wanted.
 *
 * Shaped and positioned like ColumnFilterMenu so the two read as the same kind
 * of thing: portalled to the body (a sticky header clips, and a popover under
 * the data it acts on is no use), measured after paint, flipped up when it
 * would fall off the bottom.
 *
 * Opened by a LEFT click on the header, unlike the filter menu's right click.
 * The click was already the sort gesture; this replaces what it did rather
 * than adding a gesture to learn.
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './SortOptionsMenu.css';

/**
 * Open/anchor state, so a screen does not hand-roll it per header.
 *
 * `headerProps(key)` goes on the <th>; the menu reads `menu.key` to decide
 * whether it is the one open.
 */
export function useSortMenu() {
  const [menu, setMenu] = useState({ key: null, anchor: null });
  const close = () => setMenu({ key: null, anchor: null });
  return {
    ...menu,
    close,
    open: (key, anchor) => setMenu({ key, anchor }),
    headerProps: (key) => ({
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        setMenu((prev) => (prev.key === key ? { key: null, anchor: null } : { key, anchor: e.currentTarget }));
      }
    })
  };
}

/**
 * @param {object}   menu       from useSortMenu().
 * @param {string}   menuKey    which header this instance belongs to.
 * @param {string}   title      heading, e.g. "Sort by customer".
 * @param {array}    fields     [{ value, label }] — what to sort ON.
 * @param {string}   field      the current sort field.
 * @param {string}   direction  'asc' | 'desc'.
 * @param {function} onChange   (field, direction) => void.
 * @param {object}   [labels]   { asc, desc } — "A to Z" reads better than
 *                   "Ascending" on a name and worse on a date, so the caller
 *                   says which words fit its column.
 */
function SortOptionsMenu({ menu, menuKey, title, fields, field, direction, onChange, labels }) {
  const boxRef = useRef(null);
  const [position, setPosition] = useState(null);
  const isOpen = menu.key === menuKey;

  useLayoutEffect(() => {
    if (!isOpen || !menu.anchor) { setPosition(null); return; }
    const rect = menu.anchor.getBoundingClientRect();
    const box = boxRef.current;
    const height = box?.offsetHeight || 220;
    const width = box?.offsetWidth || 220;
    const left = Math.min(rect.left, window.innerWidth - width - 8);
    const below = rect.bottom + 4;
    const top = below + height > window.innerHeight - 8
      ? Math.max(8, rect.top - height - 4)
      : below;
    setPosition({ top, left: Math.max(8, left) });
  }, [isOpen, menu.anchor]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) menu.close(); };
    const onKey = (e) => { if (e.key === 'Escape') menu.close(); };
    // Positioned once against the header, so a scrolled table would leave it
    // pointing at nothing.
    const onScroll = (e) => { if (!boxRef.current?.contains(e.target)) menu.close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', menu.close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', menu.close);
    };
  }, [isOpen, menu]);

  if (!isOpen) return null;

  const asc = labels?.asc || 'Ascending';
  const desc = labels?.desc || 'Descending';

  const row = (active, label, onClick, icon) => (
    <button type="button" className={`som-option${active ? ' active' : ''}`} onClick={onClick}>
      <span className="material-symbols-rounded som-icon">{icon}</span>
      <span className="som-label">{label}</span>
      {active && <span className="material-symbols-rounded som-tick">check</span>}
    </button>
  );

  return ReactDOM.createPortal(
    <div
      ref={boxRef}
      className="sort-options-menu"
      style={position ? { top: position.top, left: position.left } : { visibility: 'hidden' }}
      role="dialog"
      aria-label={title}
    >
      <div className="som-head">{title}</div>

      <div className="som-group">
        {fields.map((option) => row(
          field === option.value,
          option.label,
          // Choosing a different field keeps the direction: someone switching
          // from name to account number wants the same end of the list.
          () => { onChange(option.value, direction); menu.close(); },
          'sort_by_alpha'
        ))}
      </div>

      <div className="som-group">
        {row(direction === 'asc', asc, () => { onChange(field, 'asc'); menu.close(); }, 'arrow_upward')}
        {row(direction === 'desc', desc, () => { onChange(field, 'desc'); menu.close(); }, 'arrow_downward')}
      </div>
    </div>,
    document.body
  );
}

export default SortOptionsMenu;
